import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, Instagram, Play } from "lucide-react";
import { SITE } from "@/constants/site";
import { HERO } from "@/constants/testIds";

const line = {
  hidden: { y: "110%", opacity: 0 },
  show: (i = 0) => ({
    y: "0%",
    opacity: 1,
    transition: {
      duration: 0.9,
      delay: 0.15 + i * 0.09,
      ease: [0.22, 1, 0.36, 1],
    },
  }),
};

const RevealLine = ({ children, i = 0, className = "" }) => (
  <span className="reveal-mask block">
    <motion.span
      variants={line}
      initial="hidden"
      animate="show"
      custom={i}
      className={`block ${className}`}
    >
      {children}
    </motion.span>
  </span>
);

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const videoY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const videoScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "-15%"]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);
  const logoRotate = useTransform(scrollYProgress, [0, 1], [0, 90]);

  return (
    <section
      ref={ref}
      data-testid={HERO.root}
      className="hero-canvas relative w-full min-h-[100svh] bg-black overflow-hidden"
    >
      {/* Logo as background — parallax */}
      <motion.div
        style={{ y: videoY, scale: videoScale }}
        className="absolute inset-0 z-0 flex items-center justify-center"
        aria-hidden="true"
      >
        <div className="absolute inset-0 bg-[#0A0A0A]" />
        <img
          src={SITE.logo}
          alt=""
          className="relative w-[85vmin] h-[85vmin] max-w-none object-contain opacity-[0.55] select-none"
        />
        <div className="absolute inset-0 bg-black/45" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-black/30" />
      </motion.div>

      {/* Faint sniper grid */}
      <div
        className="absolute inset-0 z-[1] opacity-[0.08] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)",
          backgroundSize: "80px 80px",
        }}
      />

      <motion.div
        style={{ y: contentY, opacity: contentOpacity }}
        className="relative z-10 mx-auto max-w-[1440px] px-5 md:px-10 pt-40 md:pt-44 pb-24 min-h-[100svh] flex flex-col"
      >
        {/* Top chip row */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-10 md:mb-16">
          <div className="chip" data-testid={HERO.meaning}>
            <span className="dot" />
            The Work Gone Always Show
          </div>
          <div className="hidden md:flex items-center gap-6 font-mono-plex text-[11px] tracking-[0.28em] uppercase text-zinc-400">
            <span>N 34.05° · W 118.24°</span>
            <span>Est. Athletics + Dev LLC</span>
          </div>
        </div>

        {/* Kinetic headline */}
        <h1
          data-testid={HERO.headline}
          className="font-display uppercase text-white leading-[0.82] tracking-tight text-[18vw] md:text-[13vw] lg:text-[11.5vw] max-w-[95%]"
        >
          <RevealLine i={0}>Become more</RevealLine>
          <RevealLine i={1} className="stroke-fire">
            than an athlete.
          </RevealLine>
        </h1>

        {/* Slogan strip */}
        <div
          data-testid={HERO.slogan}
          className="mt-10 md:mt-16 flex flex-wrap items-baseline gap-x-6 gap-y-2"
        >
          {SITE.slogan.map((w, i) => (
            <span
              key={w}
              className="reveal-mask block overflow-hidden"
            >
              <motion.span
                variants={line}
                initial="hidden"
                animate="show"
                custom={4 + i}
                className="block font-display text-3xl md:text-5xl uppercase tracking-tight"
                style={{ color: i % 2 === 0 ? "#FFFFFF" : "#FF4D00" }}
              >
                {w}
              </motion.span>
            </span>
          ))}
        </div>

        {/* Bottom row */}
        <div className="mt-auto pt-16 md:pt-24 grid grid-cols-1 md:grid-cols-12 items-end gap-8">
          <div className="md:col-span-5">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.1, duration: 0.8 }}
              className="text-zinc-300 text-base md:text-lg leading-relaxed max-w-[52ch]"
            >
              A development program for student-athletes serious about their
              craft. Sports, school, brand, and life — engineered on and off
              the field.
            </motion.p>
          </div>

          <div className="md:col-span-4 md:col-start-7 flex flex-wrap gap-3">
            <Link
              to="/contact"
              data-testid={HERO.ctaStart}
              className="btn-fire"
            >
              Join The Program
              <ArrowUpRight size={18} />
            </Link>
            <a
              href={SITE.instagramUrl}
              target="_blank"
              rel="noreferrer"
              data-testid={HERO.ctaInstagram}
              className="btn-ghost"
            >
              DM {SITE.instagramHandle}
              <Instagram size={16} />
            </a>
          </div>
        </div>

        {/* Scroll hint */}
        <motion.div
          data-testid={HERO.scrollHint}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.6, duration: 1 }}
          className="absolute left-5 md:left-10 bottom-6 flex items-center gap-3 font-mono-plex text-[10px] tracking-[0.28em] uppercase text-zinc-500"
        >
          <Play size={12} className="text-[#FF4D00] fill-[#FF4D00]" />
          Scroll · Chapter 01
        </motion.div>
      </motion.div>
    </section>
  );
};

export default Hero;
